@if (canViewStorageUsage()) {
<div class="document-storage-card mx-lg-4 mx-md-4 mx-sm-0 mb-3 px-sm-4 px-3">
    <ms-document-storage-usage [usage]="storageUsage()" [isLoading]="isStorageUsageLoading()"
        (clearTrashRequested)="onClearTrashRequested()" />
</div>
}

<div class="main-content mb-4 mx-lg-4 mx-md-4 mx-sm-0 px-sm-4 px-3">

    <div class="page-title-wrapper">
        <div class="d-flex justify-content-between flex-row flex-wrap gap-3 align-items-start">

            <div class="btn-group" role="group" aria-label="Browse, search, view document activity, or switch layout">
                <button type="button" class="btn" [class.btn-primary]="viewMode() === DocumentViewMode.Browse"
                    [class.btn-ms-secondary]="viewMode() !== DocumentViewMode.Browse" [attr.aria-pressed]="viewMode() === DocumentViewMode.Browse"
                    (click)="setBrowseMode()">
                    <fa-duotone-icon icon="folder-tree" secondaryOpacity="0.4" class="me-1 d-none d-sm-inline" />
                    Browse
                </button>
                <button type="button" class="btn" [class.btn-primary]="isSearchMode()"
                    [class.btn-ms-secondary]="!isSearchMode()" [attr.aria-pressed]="isSearchMode()"
                    (click)="enterSearchMode()">
                    <fa-duotone-icon icon="magnifying-glass" secondaryOpacity="0.4"
                        class="me-1 d-none d-sm-inline" />
                    Search
                </button>

                @if (canViewActivityLog()) {
                <button type="button" class="btn" [class.btn-primary]="isActivityMode()"
                    [class.btn-ms-secondary]="!isActivityMode()" [attr.aria-pressed]="isActivityMode()"
                    (click)="enterActivityMode()">
                    <fa-duotone-icon icon="clock-rotate-left" secondaryOpacity="0.4"
                        class="me-1 d-none d-sm-inline" />
                    Activity Log
                </button>
                }

                <div class="vr mx-2 d-none d-sm-block" aria-hidden="true"></div>

                <button type="button" class="btn d-none d-sm-inline-flex align-items-center justify-content-center"
                    [class.btn-primary]="viewLayout() === DocumentViewLayout.List"
                    [class.btn-ms-secondary]="viewLayout() !== DocumentViewLayout.List"
                    [attr.aria-pressed]="viewLayout() === DocumentViewLayout.List"
                    [msTooltip]="'List view'" aria-label="List view" (click)="setViewLayout(DocumentViewLayout.List, $event)">
                    <fa-duotone-icon icon="list" secondaryOpacity="0.9" />
                </button>
                <button type="button" class="btn d-none d-sm-inline-flex align-items-center justify-content-center"
                    [class.btn-primary]="viewLayout() === DocumentViewLayout.Grid"
                    [class.btn-ms-secondary]="viewLayout() !== DocumentViewLayout.Grid"
                    [attr.aria-pressed]="viewLayout() === DocumentViewLayout.Grid"
                    [msTooltip]="'Grid view'" aria-label="Grid view" (click)="setViewLayout(DocumentViewLayout.Grid, $event)">
                    <fa-duotone-icon icon="table-cells" secondaryOpacity="0.9" />
                </button>
            </div>

            @if (showToolbar()) {
            <ms-document-toolbar [mode]="isSearchMode() ? DocumentViewMode.Search : DocumentViewMode.Browse" [filterLabel]="filterScopeLabel()"
                [canFilterFiles]="canFilterFiles()" [canFilterByAccess]="canFilterByAccess()" [filter]="filter()"
                (filterChange)="filter.set($event)" [sort]="sort()" (sortChange)="onGlobalSortChanged($event)"
                [permissionEligibleCount]="selectedTopLevelFolderCount()" [moveEligibleCount]="selectedFileCount()"
                [deleteFolderEligibleCount]="selectedFolderCount()" [deleteFileEligibleCount]="deleteEligibleFileCount()"
                [canUpload]="canUpload()" (uploadFilesRequested)="onUploadFilesRequested($event)"
                (newFolderRequested)="onNewFolderRequested()" (bulkPermissionsRequested)="onBulkPermissionsRequested()"
                (bulkMoveRequested)="onBulkMoveRequested()" (bulkDeleteFoldersRequested)="onBulkDeleteFoldersRequested()"
                (bulkDeleteFilesRequested)="onBulkDeleteFilesRequested()" />
            }
        </div>

        @if (isSearchMode()) {
        <div class="mt-4">
            <div class="input-group position-relative">
                <input #searchInput id="documentsSearch" type="text"
                    class="form-control form-control-solid ps-13 pe-5" placeholder="Search Documents"
                    aria-label="Search Documents" [ngModel]="searchTerm()" (ngModelChange)="searchTerm.set($event)"
                    (keyup.enter)="onSearch()" aria-describedby="documents-search-description" />

                @if (searchTerm()) {
                <button
                    class="btn btn-link position-absolute top-50 translate-middle-y p-0 border-0 bg-transparent mx-2 input-group-clear-button"
                    type="button" (click)="clearSearch()" aria-label="Clear search">
                    <fa-duotone-icon icon="xmark" class="text-secondary fs-6" secondaryOpacity="0.9" size="lg" />
                </button>
                }

                <div class="input-group-append">
                    <button class="btn btn-primary rounded-0 rounded-end" type="button" (click)="onSearch()">
                        Search
                    </button>
                </div>
            </div>
            <div id="documents-search-description" class="ps-2 mt-2 text-muted small fst-italic">
                Search by name
            </div>
        </div>
        }
    </div>

    @if (isActivityMode()) {
    <div class="card border-0 shadow-sm mb-4">
        <div class="card-body p-4">
            <ms-audit-logs [entityId]="currentOrganizationId()" [entityType]="AuditEntityType.DocumentCenter" />
        </div>
    </div>
    } @else {

    @if (isSearchMode()) {
    <div class="current-folder-header mb-3">
        <h4 class="m-0 d-flex align-items-center gap-2">
            <fa-duotone-icon icon="magnifying-glass" class="text-secondary" secondaryOpacity="0.4" />
            <span>Search results</span>
        </h4>
        <div class="text-secondary small mt-1">
            @if (effectiveSearchTerm()) {
            Matches for "{{ effectiveSearchTerm() }}" across all folders
            } @else {
            {{ DocumentsMessagingConstants.DOCUMENTS_SEARCH_PROMPT() }}
            }
        </div>
    </div>
    } @else if (currentFolder()) {
    <div class="current-folder-header mb-3">
        <div class="d-flex align-items-center flex-wrap gap-2 mb-3">
            @if (showRootButton()) {
            <button type="button" class="btn btn-ms-secondary folder-nav-btn" [class.btn-sm]="!isMobile()"
                (click)="goToRoot()">
                <fa-duotone-icon icon="house" secondaryOpacity="0.3" class="me-1 flex-shrink-0" />
                {{ DocumentsMessagingConstants.DOCUMENTS_ROOT_LABEL() }}
            </button>
            }
            <button type="button" class="btn btn-ms-secondary folder-nav-btn" [class.btn-sm]="!isMobile()"
                (click)="goToParent()">
                <fa-duotone-icon icon="arrow-left" secondaryOpacity="0.9" class="me-1 flex-shrink-0" />
                <span class="text-truncate">Back to {{ parentLabel() }}</span>
            </button>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
            <h4 class="m-0 d-flex align-items-center gap-2 folder-title">
                <fa-duotone-icon icon="folder" class="text-warning flex-shrink-0" secondaryOpacity="0.9" />
                <span class="text-truncate">{{ currentFolder()!.name }}</span>
            </h4>
            <span [class]="DocumentUtilities.permissionBadgeClass(currentFolder()!.permission)">
                <fa-duotone-icon [icon]="DocumentUtilities.permissionIcon(currentFolder()!.permission)"
                    secondaryOpacity="0.4" class="me-1" />
                {{ DocumentUtilities.permissionLabel(currentFolder()!.permission) }}
            </span>
        </div>
        <div class="text-secondary small mt-1">
            Created {{ currentFolder()!.created | orgDate:'mediumDate' }}
            · Last modified {{ currentFolder()!.lastModified | orgDate:'mediumDate' }}
        </div>
    </div>
    }

    @if (isSearchMode() && !effectiveSearchTerm()) {
    <div class="text-center py-5">
        <fa-duotone-icon icon="magnifying-glass" size="3x" secondaryOpacity="0.4" class="text-secondary mb-3" />
        <h5 class="fw-semibold mb-1">{{ DocumentsMessagingConstants.DOCUMENTS_SEARCH_PROMPT_TITLE() }}</h5>
        <p class="text-secondary mb-0">{{ DocumentsMessagingConstants.DOCUMENTS_SEARCH_PROMPT() }}</p>
    </div>
    } @else {
    <div class="documents-list-wrapper">
        <ms-document-list [items]="displayItems()" [selectedFolderIds]="selectedFolderIds()" [selectedFileIds]="selectedFileIds()" [isLoading]="isLoading()"
            [viewLayout]="viewLayout()" [showBreadcrumbColumn]="showBreadcrumbColumn()" [isRoot]="currentFolderId() == null"
            [hasMoreFiles]="hasMoreFiles()" [isLoadingMoreFiles]="isLoadingMoreFiles()"
            [allFoldersSelected]="allFoldersSelected()" [allFilesSelected]="isAllFilesSelected()"
            [downloadingId]="downloadingId()"
            [folderSort]="folderSort()" (folderSortChanged)="onFolderSortChanged($event)"
            [fileSort]="fileSort()" (fileSortChanged)="onFileSortChanged($event)"
            [listFileItems]="listFileItems()" [isFilesRefreshing]="isFilesRefreshing()"
            [listHasMoreFiles]="listHasMoreFiles()" [listIsLoadingMoreFiles]="listIsLoadingMoreFiles()"
            (loadMoreListFiles)="onLoadMoreListFiles()"
            (folderOpened)="onFolderOpened($event)"
            (fileOpened)="onDownloadRequested($event)" (downloadRequested)="onDownloadRequested($event)"
            (selectionToggled)="onSelectionToggled($event)" (renameRequested)="onRenameRequested($event)"
            (moveRequested)="onMoveRequested($event)" (permissionsRequested)="onPermissionsRequested($event)"
            (deleteRequested)="onDeleteRequested($event)" (loadMoreFiles)="onLoadMoreFiles()"
            (selectAllFoldersToggled)="onSelectAllFoldersToggled($event)" (selectAllFilesToggled)="onSelectAllFilesToggled($event)" />
    </div>
    }

    }

    <ms-document-upload-overlay [uploads]="uploadProgress()" [isDragging]="isDragging()"
        (cancelUpload)="uploadManager.cancel($event)" (retryUpload)="uploadManager.retry($event)"
        (closeDock)="uploadManager.dismissCompleted()" />

    <ms-document-permissions-modal #permsModalRef [targets]="permissionsTargets()"
        (submitted)="onPermissionsSubmitted($event)" (cancelled)="onPermissionsCancelled()" />

    <ms-document-rename-modal #renameModalRef (submitted)="onRenameSubmitted($event)" />

    <ms-document-move-folder-modal #moveModalRef [sourceFolder]="moveTarget()" [folderTree]="folderTree()"
        (submitted)="onMoveSubmitted($event)" (cancelled)="onMoveCancelled()" />

    <ms-document-bulk-move-files-modal #bulkMoveModalRef [fileNodes]="moveFileTargets()" [folderTree]="folderTree()"
        (submitted)="onBulkMoveSubmitted($event)" (cancelled)="onBulkMoveCancelled()" />

    <ms-confirmation-modal #deleteConfirmRef [modalId]="deleteModalId" [title]="deleteActionLabel()"
        [message]="deleteConfirmMessage()" [confirmButtonText]="deleteActionLabel()" confirmButtonClassName="btn-danger"
        [(isSubmitting)]="isDeleteSubmitting" (confirmed)="onDeleteConfirmed()" (cancelled)="onDeleteCancelled()" />

    <ms-confirmation-modal [modalId]="bulkDeleteFoldersModalId" [title]="bulkDeleteFoldersTitle()"
        [message]="DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FOLDERS_CONFIRMATION()"
        [enableTypeToConfirm]="true" typeToConfirmText="BULK DELETE" confirmButtonText="Delete" confirmButtonClassName="btn-danger"
        [(isSubmitting)]="isBulkDeleteFoldersSubmitting" (confirmed)="onBulkDeleteFoldersConfirmed()" (cancelled)="onBulkDeleteFoldersCancelled()" />

    <ms-confirmation-modal [modalId]="bulkDeleteFilesModalId" [title]="bulkDeleteFilesTitle()"
        [message]="DocumentsMessagingConstants.DOCUMENTS_BULK_DELETE_FILES_CONFIRMATION()"
        [enableTypeToConfirm]="true" typeToConfirmText="BULK DELETE" confirmButtonText="Delete" confirmButtonClassName="btn-danger"
        [(isSubmitting)]="isBulkDeleteFilesSubmitting" (confirmed)="onBulkDeleteFilesConfirmed()" (cancelled)="onBulkDeleteFilesCancelled()" />

    <ms-confirmation-modal [modalId]="emptyTrashModalId"
        [title]="DocumentsMessagingConstants.DOCUMENTS_EMPTY_TRASH_TITLE()"
        [message]="DocumentsMessagingConstants.DOCUMENTS_EMPTY_TRASH_CONFIRMATION()"
        [confirmButtonText]="DocumentsMessagingConstants.DOCUMENTS_EMPTY_TRASH_BUTTON()" confirmButtonClassName="btn-danger"
        [(isSubmitting)]="isEmptyTrashSubmitting" (confirmed)="onEmptyTrashConfirmed()" (cancelled)="onEmptyTrashCancelled()" />

    <ms-confirmation-modal [modalId]="leaveConfirmModalId"
        [title]="DocumentsMessagingConstants.DOCUMENTS_UPLOAD_IN_PROGRESS_TITLE()"
        [message]="DocumentsMessagingConstants.DOCUMENTS_UPLOAD_LEAVE_CONFIRM()" confirmButtonText="Leave"
        cancelButtonText="Stay" confirmButtonClassName="btn-danger" (confirmed)="onLeaveConfirmed()"
        (cancelled)="onLeaveCancelled()" />

    <ms-info-modal #infoModalRef [modalId]="infoModalId" [title]="infoTitle()" [message]="infoMessage()"
        [modalType]="infoModalType()" />
</div>